import React, { useMemo } from 'react';
import type { CapexCaptureWidgetInstanceConfig, CapexCaptureData } from './types';
import {
  formatPrice,
  formatCompact,
  axisCompact,
  buildTimeAxisFormatter,
  SEMANTIC_CHART_COLORS,
} from '../../../design';
import type { AxisLabelFormatterParams } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, KeyMetricBadge } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';

// Preview data (no asset assigned): the NVDA capture story, 2022–2025.
const SAMPLE_DATA: CapexCaptureData = {
  ticker: 'NVDA',
  latest_revenue: 130500000000,
  latest_total_capex: 357500000000,
  latest_pct_capture: 0.365,
  revenue_series: [
    { date: '2022-12-31', revenue: 26900000000 },
    { date: '2023-12-31', revenue: 27000000000 },
    { date: '2024-12-31', revenue: 60900000000 },
    { date: '2025-12-31', revenue: 130500000000 },
  ],
  capex_series: [
    { date: '2022-12-31', total_capex: 150400000000 },
    { date: '2023-12-31', total_capex: 140400000000 },
    { date: '2024-12-31', total_capex: 217300000000 },
    { date: '2025-12-31', total_capex: 357500000000 },
  ],
  capture_series: [
    { date: '2022-12-31', pct_capture: 0.179 },
    { date: '2023-12-31', pct_capture: 0.192 },
    { date: '2024-12-31', pct_capture: 0.280 },
    { date: '2025-12-31', pct_capture: 0.365 },
  ],
};

interface Props {
  config: CapexCaptureWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const axisPctCapture = (params: AxisLabelFormatterParams): string =>
  params.value == null ? '' : `${(Number(params.value) * 100).toFixed(0)}%`;

const CapexCaptureWidgetRenderer: React.FC<Props> = ({ config, dashboardData = {}, widgetInstanceId }) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<CapexCaptureData>(
    'capex_capture',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  const ticker = displayData?.ticker || 'Asset';

  // Merge the three date-keyed series into unified rows, sorted ascending.
  const chartData = useMemo(() => {
    if (!displayData?.revenue_series) return [];
    const capexMap = new Map<string, number>();
    for (const r of displayData.capex_series ?? []) capexMap.set(r.date, r.total_capex);
    const captureMap = new Map<string, number>();
    for (const r of displayData.capture_series ?? []) captureMap.set(r.date, r.pct_capture);

    const points = displayData.revenue_series.map((r) => {
      const point: Record<string, unknown> = { date: r.date, revenue: r.revenue };
      const cap = capexMap.get(r.date);
      if (cap !== undefined) point.total_capex = cap;
      const cpt = captureMap.get(r.date);
      if (cpt !== undefined) point.pct_capture = cpt;
      return point;
    });
    points.sort((a, b) => (a.date as string).localeCompare(b.date as string));
    return points;
  }, [displayData]);

  // Revenue + capex share the left $ axis; % capture rides the right axis.
  const series = useMemo<LineChartSeries[]>(() => [
    { yKey: 'revenue', yName: `${ticker} Revenue`, axis: 'rev', color: SEMANTIC_CHART_COLORS.subject, markers: true },
    { yKey: 'total_capex', yName: 'Hyperscaler CapEx', axis: 'rev', color: SEMANTIC_CHART_COLORS.benchmark, markers: true },
    { yKey: 'pct_capture', yName: '% Capture', axis: 'pct', color: SEMANTIC_CHART_COLORS.caution, markers: true },
  ], [ticker]);

  const dates = useMemo(() => chartData.map((d) => d.date as string), [chartData]);
  const timeFormatter = useMemo(() => buildTimeAxisFormatter(dates), [dates]);

  const legendSeries = useMemo<ChartLegendSeries[]>(() => {
    const last = chartData.length ? chartData[chartData.length - 1] : null;
    return series.map((s) => ({
      key: s.yKey,
      name: s.yName,
      color: s.color || SEMANTIC_CHART_COLORS.subject,
      value: last && typeof last[s.yKey] === 'number' ? (last[s.yKey] as number) : null,
      formatter: (v: number | string) =>
        s.yKey === 'pct_capture' ? `${(Number(v) * 100).toFixed(1)}%` : formatPrice(Number(v), 0),
    }));
  }, [series, chartData]);

  const spec: ChartSpec = {
    chartKind: 'line',
    rows: chartData,
    xKey: 'date',
    lineSeries: series,
    showEndValueTags: true,
    xLabelFormatter: timeFormatter,
    xTitle: 'Year',
    yLabelFormatters: { rev: axisCompact, pct: axisPctCapture },
    yTitles: { rev: 'Revenue / CapEx ($)', pct: '% Capture' },
    yAxisColors: { pct: SEMANTIC_CHART_COLORS.caution },
    legendSeries,
    footnote: 'Source: FMP  ·  Hyperscaler CapEx = MSFT + GOOGL + AMZN + META',
    emptyMessage: 'No data available',
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <div className="min-w-0 mr-1 flex-1">
        <h3 className="text-sm font-semibold text-neutral-900 truncate">Revenue vs Hyperscaler CapEx</h3>
        <div className="text-[11px] text-neutral-400 truncate">
          <span className="uppercase tracking-wide">{ticker} · % Capture · annual</span>
        </div>
      </div>
      <KeyMetricBadge label="Revenue" value={formatPrice(displayData?.latest_revenue ?? null, 0)} />
      <KeyMetricBadge label="HS CapEx" value={formatPrice(displayData?.latest_total_capex ?? null, 0)} />
      <KeyMetricBadge
        label="% Capture"
        value={displayData?.latest_pct_capture != null ? `${(displayData.latest_pct_capture * 100).toFixed(1)}%` : '--'}
      />
    </div>
  );

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      toolbar={header}
    />
  );
};

export default CapexCaptureWidgetRenderer;
